import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BadgePercent } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/seo";

export default function OfferClaim() {
  usePageMeta({ title: "Your trial offer | Solis Casting", noindex: true });
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user } = useAuth();
  const [state, setState] = useState("idle"); // idle | claiming | done
  const [error, setError] = useState("");

  const claim = async () => {
    setState("claiming");
    setError("");
    try {
      await api.post("/payments/claim-trial-offer", { token });
      setState("done");
      toast.success("50% off applied — your first month is $24.50.");
    } catch (e) {
      setError(fmtErr(e));
      setState("idle");
    }
  };

  return (
    <div className="px-6 sm:px-10 py-32 max-w-2xl" data-testid="offer-claim-page">
      <p className="overline-label mb-6">Trial offer</p>
      <h1 className="font-display font-light text-4xl sm:text-6xl tracking-tighter leading-[0.95] mb-8">
        Half off your <span className="italic text-accent">first month.</span>
      </h1>
      <p className="text-white/70 text-base mb-10 max-w-xl">
        Stay past your free trial and your first month of Starter is <strong className="text-white">50% off — $24.50 instead of $49.00</strong>.
        The discount applies to your subscription automatically, right when the trial converts.
      </p>

      {user === undefined ? null : !user ? (
        <div data-testid="offer-claim-signin">
          <p className="text-white/60 text-sm mb-6">Sign in with the account that received this offer to claim it.</p>
          <Link to={`/auth?next=/offer/claim?token=${token}`} data-testid="offer-claim-signin-btn" className="btn-primary">Sign in to claim</Link>
        </div>
      ) : state === "done" ? (
        <div className="panel p-8" data-testid="offer-claim-success">
          <p className="font-display text-3xl mb-3">It's <span className="italic text-accent">yours.</span></p>
          <p className="text-white/70 text-sm mb-8">50% off is locked in — when your trial converts, your first month bills at $24.50. Your calls stay live and every applicant stays in the room.</p>
          <Link to="/dashboard" data-testid="offer-claim-dashboard-btn" className="btn-primary">Back to your atelier</Link>
        </div>
      ) : (
        <div data-testid="offer-claim-cta">
          {error && <p className="text-accent text-sm mb-6" data-testid="offer-claim-error">{error}</p>}
          <button data-testid="offer-claim-button" onClick={claim} disabled={state === "claiming" || !token} className="btn-primary disabled:opacity-40">
            <BadgePercent size={16} /> {state === "claiming" ? "Applying…" : "Claim 50% off"}
          </button>
        </div>
      )}
    </div>
  );
}
